import { db } from "@/main";
import {
  defineOwnCompanySettingsClientAccess,
  defineOwnAllDevicesSettingsClientAccess,
  defineOwnHyDryDeviceSettingsClientAccess,
  defineOwnProjectManagementSettingsClientAccess,
  defineOwnReportSettingsClientAccess,
  defineOwnUserSettingsClientAccess,
  defineCategoriesAndTemplatesClientAccess,
  defineOwnHyDryMessagesSettingsClientAccess,
  canMemberDefineOwnUserSettings,
  defineOwnSystemConfigurationClientAccess,
} from "@/permissions/ClientPermissions";
import { CompanySettingsModule } from "@/store/modules/CompanySettingsModule";
import { HyDryDeviceSettingsModule } from "@/store/modules/HyDryDeviceSettingsModule";
import { HyDryMessagesSettingsModule } from "@/store/modules/HyDryMessagesSettingsModule";
import { LocalSettingsModule } from "@/store/modules/LocalSettingsModule";
import { ProjectManagementSettingsModule } from "@/store/modules/ProjectManagementSettingsModule";
import { UserSettingsModule } from "@/store/modules/UserSettingsModule";
import { filterOutUndefined } from "@/utils/Utils";
import {
  ComBoxHumidityUnit,
  getDefaultHumidityUnit,
} from "../models/ComBoxHumidityUnit";
import {
  DeviceCategoriesAndTemplates,
  DeviceSettingsJson,
} from "../models/DeviceSettingsManager";
import { DeviceCategoryTemplate } from "../models/DeviceCategoryTemplate";
import { HyDryMessagesSettings } from "../models/HyDryMessagesConfig";
import {
  AllDevicesSettings,
  CompanySettings,
  FinishedProjectsSettings,
  HyDryDeviceSettings,
  ProjectManagementSettings,
  ReportSettings,
  UserSettings,
  withoutProjectOrganisation,
} from "../models/Settings";
import {
  getClientId,
  getClientRef,
  getClientsAdministrationRef,
  getClientsAdministrationRefByAdministratorId,
  getComBoxClientConfigInfo,
  getClientRefById,
} from "./ClientRepository";
import Client from "../models/Client";

export function getSettingsRef() {
  return db
    .doc(getClientRef())
    .collection("settings");
}

/**
 * Retrieves a Firestore document reference for report settings, determining the appropriate reference based on
 * the context of client administration or specific client settings. This function evaluates several conditions
 * to decide whether to use a general client administration reference, a specific client reference, or fallback
 * to a default client reference. The decision is influenced by the presence or absence of a client administration
 * reference, the result of a function determining access to own report settings, and an optional boolean parameter
 * that indicates a preference for using the caller's own reference.
 *
 * If the `clientsAdministrationRef` is empty, the `defineOwnReportSettingsClientAccess` function allows access,
 * or if the `ownRef` parameter is true, then the function uses the specific client reference obtained from
 * `getClientRef()`. Otherwise, it defaults to using the `clientsAdministrationRef`. The final document reference
 * points to a "reportSettings" document within a "settings" collection of the chosen reference path in the database.
 *
 * @param {boolean} [ownRef=false] - Optional. Specifies whether to force the use of the caller's own client reference
 *                                   over the general client administration reference. Defaults to false, meaning
 *                                   the general administration reference is used unless other conditions apply.
 * @returns {FirebaseFirestore.DocumentReference} A Firestore document reference to the "reportSettings" document
 *                                                within the determined reference path's "settings" collection.
 */
function getReportSettingsRef(ownRef?: boolean) {
  const clientsAdministrationRef = getClientsAdministrationRef();

  const documentRef =
    clientsAdministrationRef === "" ||
    defineOwnReportSettingsClientAccess() ||
    ownRef
      ? getClientRef()
      : clientsAdministrationRef;

  return db
    .doc(documentRef)
    .collection("settings")
    .doc("reportSettings");
}

/**
 * Asynchronously loads the current report settings from the database. This function retrieves the report settings
 * using a document reference obtained from `getReportSettingsRef()`. If the document exists, its data is returned
 * as an instance of `ReportSettings`.
 *
 * @returns {Promise<ReportSettings | undefined>} A promise that resolves to the report settings if found, or
 *                                                undefined if the document does not exist. The `ReportSettings`
 *                                                type should reflect the structure of report settings data stored
 *                                                in the database.
 */
export async function loadReportSettings() {
  const result = await getReportSettingsRef().get();

  if (result) {
    return result.data() as ReportSettings;
  }
}

/**
 * Sets up a real-time listener for changes to the report settings document in the database. When the report settings
 * document is updated, the provided callback function is invoked with the new report settings data.
 *
 * @param {function(reportSettings: ReportSettings): void} onNewReportSettings - A callback function that is called
 *                                                                               whenever the report settings document
 *                                                                               is updated. The updated report settings
 *                                                                               are passed to this callback as an argument.
 * @returns {function} A function that, when called, will unsubscribe from further document updates. This allows
 *                     for cleanup when the listener is no longer needed.
 */
export function observeReportSettings(
  onNewReportSettings: (reportSettings: ReportSettings) => void
) {
  return getReportSettingsRef().onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const reportSettings = snapshot.data() as ReportSettings;
      onNewReportSettings(reportSettings);
    }
  });
}

/**
 * Asynchronously updates the report settings in the database with the provided update data. This function merges
 * the provided update object with the existing document, allowing for partial updates without overwriting unrelated
 * fields. The operation ensures that only the fields specified in the update object are modified.
 *
 * @param {Partial<ReportSettings>} update - An object containing the new values for the report settings. This object
 *                                  does not need to contain every field in the report settings, as unspecified
 *                                  fields will remain unchanged thanks to the merge operation.
 * @returns {Promise<boolean>} A promise that resolves to true when the update operation completes successfully.
 */
export async function updateReportSettings(update: Partial<ReportSettings>) {
  // Filter out any properties that should not be updated directly
  if (update.areDefaultReportSettings !== undefined) {
    update.areDefaultReportSettings = undefined;
  }

  await getReportSettingsRef(true).set(filterOutUndefined(update), { merge: true });

  return true;
}

/**
 * Retrieves a Firestore document reference for the user settings, determining the appropriate reference based on
 * the context of client administration or specific client settings. The logic to decide whether to use the
 * client's specific reference or fall back to the client administration's reference considers the presence of a
 * clients administration reference, the outcome of a function assessing access to own user settings, and an optional
 * parameter indicating a preference for using the caller's own reference.
 *
 * If the `clientsAdministrationRef` is not set, or access to define own user settings is granted, or the `ownRef`
 * parameter is true, the function defaults to using the specific client reference obtained from `getClientRef()`.
 * Otherwise, it uses the `clientsAdministrationRef`. The returned reference points to the "userSettings" document
 * within a "settings" collection, based on the determined document reference path in the database.
 *
 * @param {boolean} [ownRef=false] - Optional. Indicates whether to prioritize the caller's own client reference over
 *                                   the general client administration reference. Defaults to false, meaning the
 *                                   administration reference is used unless overridden by conditions or the parameter.
 * @returns {FirebaseFirestore.DocumentReference} Firestore document reference to the "userSettings" document within
 *                                                the selected reference path's "settings" collection.
 */
function getUserSettingsRef(ownRef?: boolean) {
  const clientsAdministrationRef = getClientsAdministrationRef();

  const documentRef =
    clientsAdministrationRef === "" ||
    defineOwnUserSettingsClientAccess() ||
    ownRef
      ? getClientRef()
      : clientsAdministrationRef;

  return db
    .doc(documentRef)
    .collection("settings")
    .doc("userSettings");
}

/**
 * Retrieves the reference to the user settings document for a given member client.
 * 
 * @param {Client} memberClient - The client object containing necessary information such as the client ID.
 * @returns {DocumentReference} - A Firestore DocumentReference pointing to the user settings document.
 */
function getMemberClientsUserSettingsRef(memberClient: Client) {
  const clientsAdministrationRef = getClientsAdministrationRefByAdministratorId(getClientId());

  const documentRef =
    canMemberDefineOwnUserSettings(memberClient)
      ? getClientRefById(memberClient.id)
      : clientsAdministrationRef;

  return db
    .doc(documentRef)
    .collection("settings")
    .doc("userSettings");
}

/**
 * Initiates a real-time listener for updates to the user settings document in the database. Upon any update to the
 * document, the specified callback function is invoked with the updated user settings data.
 *
 * @param {function(userSettings: UserSettings): void} onNewUserSettings - Callback function that gets executed
 *                                                                         every time the user settings document is
 *                                                                         updated. It receives the updated user settings
 *                                                                         as its parameter.
 * @returns {function} A function that can be called to unsubscribe from the document updates. This is useful for
 *                     cleanup and preventing memory leaks when the real-time updates are no longer needed.
 */
export function observeUserSettings(
  onNewUserSettings: (userSettings: UserSettings) => void
) {
  return getUserSettingsRef().onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const userSettings = snapshot.data() as UserSettings;
      onNewUserSettings(userSettings);
    }
  });
}

/**
 * Retrieves the user settings for a given member client.
 * 
 * @param {Client} memberClient - The client object containing necessary information such as the client ID.
 * @returns {Promise<UserSettings | null>} - A promise that resolves to the user settings if found, or null if not.
 */

export async function getMemberClientsUserSettings(memberClient: Client): Promise<UserSettings | null> {
  const snapshot = await getMemberClientsUserSettingsRef(memberClient).get();
  if (snapshot.exists) {
    return snapshot.data() as UserSettings;
  } else {
    return null;
  }
}

/**
 * Asynchronously updates the user settings in the database with the provided update data. This function performs a
 * merge update, meaning the provided update object is merged with the existing document. This allows specific fields
 * to be updated without overwriting the entire document, preserving any fields not explicitly mentioned in the update.
 *
 * @param {Partial<UserSettings>} update - The user-settings fields to update. Being a merge update, only the
 *                                provided fields are written; unspecified fields remain unaffected.
 * @returns {Promise<boolean>} A promise that resolves to true indicating the update operation was successful.
 */
export async function updateUserSettings(update: Partial<UserSettings>) {
  // Filter out any properties that should not be updated directly
  if (update.areDefaultUserSettings !== undefined) {
    update.areDefaultUserSettings = undefined;
  }

  await getUserSettingsRef(true).set(filterOutUndefined(update), { merge: true });

  return true;
}

/**
 * Retrieves a reference to the finished projects settings document.
 * 
 * @param {boolean} [ownRef=false] - Optional flag to determine if the own reference should be used.
 * @returns {firebase.firestore.DocumentReference} - The document reference for the finished projects settings.
 */
function getFinishedProjectsSettingsRef(ownRef?: boolean) {
  const clientsAdministrationRef = getClientsAdministrationRef();

  const documentRef =
    clientsAdministrationRef === "" ||
    defineOwnSystemConfigurationClientAccess() ||
    ownRef
      ? getClientRef()
      : clientsAdministrationRef;

  return db
    .doc(documentRef)
    .collection("settings")
    .doc("finishedProjectsSettings");
}

/**
 * Observes changes to the finished projects settings and triggers the provided callback with the new settings.
 * 
 * @param {function(FinishedProjectsSettings): void} onNewFinishedProjectsSettings - Callback function to be called with the new finished projects settings.
 * @returns {function(): void} - Function to unsubscribe from the snapshot listener.
 */
export function observeFinishedProjectsSettings(
  onNewFinishedProjectsSettings: (finishedProjectsSettings: FinishedProjectsSettings) => void
) {
  return getFinishedProjectsSettingsRef().onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const finishedProjectsSettings = snapshot.data() as FinishedProjectsSettings;
      onNewFinishedProjectsSettings(finishedProjectsSettings);
    }
  });
}

/**
 * Updates the finished projects settings with the provided update.
 * 
 * @param {FinishedProjectsSettings} update - The finished projects settings to be updated.
 * @returns {Promise<boolean>} - A promise that resolves to true when the update is successful.
 */
export async function updateFinsihedProjectsSettings(
  update: FinishedProjectsSettings
) {
  // Filter out any properties that should not be updated directly
  if (update.areDefaultFinishedProjectsSettings !== undefined) {
    update.areDefaultFinishedProjectsSettings = undefined;
  }

  await getFinishedProjectsSettingsRef(true).set(filterOutUndefined(update), { merge: true });

  return true;
}

/**
 * Retrieves a Firestore document reference for the HyDry messages settings, determining the appropriate reference based on
 * the context of client administration or specific client settings. This function decides whether to use the
 * client's specific reference or fall back to the client administration's reference based on the presence of a
 * clients administration reference, the outcome of a function assessing access to own HyDry messages settings, and an optional
 * parameter indicating a preference for using the caller's own reference.
 *
 * If the `clientsAdministrationRef` is empty, or access to define own HyDry messages settings is granted, or the `ownRef`
 * parameter is true, the function defaults to using the specific client reference obtained from `getClientRef()`.
 * Otherwise, it uses the `clientsAdministrationRef`. The returned reference points to the "hyDryMessagesSettings" document
 * within a "settings" collection, based on the determined document reference path in the database.
 *
 * @param {boolean} [ownRef=false] - Optional. Indicates whether to prioritize the caller's own client reference over
 *                                   the general client administration reference. Defaults to false, meaning the
 *                                   administration reference is used unless overridden by conditions or the parameter.
 * @returns {FirebaseFirestore.DocumentReference} Firestore document reference to the "hyDryMessagesSettings" document within
 *                                                the selected reference path's "settings" collection.
 */
function getHyDryMessagesSettingsRef(ownRef?: boolean) {
  const clientsAdministrationRef = getClientsAdministrationRef();

  const documentRef =
    clientsAdministrationRef === "" ||
    defineOwnHyDryMessagesSettingsClientAccess() ||
    ownRef
      ? getClientRef()
      : clientsAdministrationRef;

  return db
    .doc(documentRef)
    .collection("settings")
    .doc("hyDryMessagesSettings");
}

/**
 * Initiates a real-time listener for updates to the HyDry messages settings document in the database. Upon any update to the
 * document, the specified callback function is invoked with the updated HyDry messages settings.
 *
 * @param {function(HyDryMessagesSettings): void} onNewHyDryMessageSettings - Callback function that gets executed
 *                                                                            every time the HyDry messages settings document is
 *                                                                            updated. It receives the updated settings
 *                                                                            as its parameter.
 * @returns {function} A function that can be called to unsubscribe from the document updates. This is useful for
 *                     cleanup and preventing memory leaks when the real-time updates are no longer needed.
 */
export function observeHyDryMessagesSettings(
  onNewHyDryMessageSettings: (hyDryMessagesSettings: HyDryMessagesSettings) => void
) {
  return getHyDryMessagesSettingsRef().onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const hyDryMessagesSettings = snapshot.data() as HyDryMessagesSettings;
      onNewHyDryMessageSettings(hyDryMessagesSettings);
    }
  });
}

/**
 * Asynchronously updates the HyDry messages settings in the database with the provided update data. This function performs a
 * merge update, meaning the provided update object is merged with the existing document. This allows specific fields
 * to be updated without overwriting the entire document, preserving any fields not explicitly mentioned in the update.
 *
 * @param {HyDryMessagesSettings} update - An object containing the updates to the HyDry messages settings. This can include changes to
 *                                         any part of the settings, with unspecified fields remaining unaffected.
 * @returns {Promise<boolean>} A promise that resolves to true indicating the update operation was successful.
 */
export async function updateHyDryMessagesSettings(update: HyDryMessagesSettings) {
  // Filter out any properties that should not be updated directly
  if (update.areDefaultHyDryMessagesSettings !== undefined) {
    update.areDefaultHyDryMessagesSettings = undefined;
  }
  
  await getHyDryMessagesSettingsRef(true).set(filterOutUndefined(update), { merge: true });

  return true;
}

/**
 * Retrieves a Firestore document reference for the company settings based on the current access level and specified
 * conditions. The function determines the most appropriate document reference for accessing or updating the
 * company settings by evaluating the existence of a clients administration reference, whether access to define
 * own company settings is granted, or if an explicit request for the caller's own reference is made through the
 * `ownRef` parameter.
 *
 * If the clients administration reference is not set, access to define own company settings is granted, or `ownRef`
 * is true, the function uses the specific client reference obtained from `getClientRef()`. Otherwise, it defaults
 * to using the clients administration reference. The returned reference points to the "companySettings" document
 * within a "settings" collection, based on the determined path in the database.
 *
 * @param {boolean} [ownRef=false] - Optional boolean to indicate a preference for using the caller's own reference
 *                                   over the general clients administration reference, defaulting to false.
 * @returns {FirebaseFirestore.DocumentReference} A Firestore document reference to the "companySettings" document
 *                                                within the determined path's "settings" collection.
 */
function getCompanySettingsRef(ownRef?: boolean) {
  const clientsAdministrationRef = getClientsAdministrationRef();

  const documentRef =
    clientsAdministrationRef === "" ||
    defineOwnCompanySettingsClientAccess() ||
    ownRef
      ? getClientRef()
      : clientsAdministrationRef;

  return db
    .doc(documentRef)
    .collection("settings")
    .doc("companySettings");
}

/**
 * Sets up a real-time listener for changes to the company settings document in the database. When the company
 * settings document is updated, the provided callback function is called with the new company settings data.
 *
 * This enables applications to react in real-time to changes in company settings, ensuring that the latest settings
 * are always used. It's particularly useful for applications that need to apply company-wide policies or configurations
 * dynamically.
 *
 * @param {function(companySettings: CompanySettings): void} onNewCompanySettings - Callback function that gets executed
 *                                                                                  whenever the company settings
 *                                                                                  document is updated. The updated
 *                                                                                  company settings are passed to this
 *                                                                                  callback as an argument.
 * @returns {function} A function that, when called, unsubscribes from further updates to the company settings document,
 *                     useful for cleanup and preventing memory leaks when updates are no longer needed.
 */
export function observeCompanySettings(
  onNewCompanySettings: (companySettings: CompanySettings) => void
) {
  return getCompanySettingsRef().onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const companySettings = snapshot.data() as CompanySettings;
      onNewCompanySettings(companySettings);
    }
  });
}

/**
 * Asynchronously updates the company settings in the database with the specified update data. The update is performed
 * as a merge, meaning that the provided object is combined with the existing document in the database, allowing
 * for partial updates to the settings without overwriting other fields.
 *
 * This approach is useful for making incremental changes to the company settings without needing to submit a complete
 * settings object, thereby preserving existing settings that are not being modified.
 *
 * @param {CompanySettings} update - An object containing the updates to the company settings. This includes any
 *                                   changes to be made, with fields not mentioned in the update object remaining
 *                                   unchanged thanks to the merge strategy.
 * @returns {Promise<boolean>} A promise that resolves to true to indicate the update operation was successful.
 */
export async function updateCompanySettings(update: Partial<CompanySettings>) {
  // Filter out any properties that should not be updated directly
  if (update.areDefaultCompanySettings !== undefined) {
    update.areDefaultCompanySettings = undefined;
  }

  await getCompanySettingsRef(true).set(filterOutUndefined(update), { merge: true });

  return true;
}

/**
 * Retrieves a Firestore document reference for the HyDry device settings based on the current client access level and specified conditions.
 * It determines the most appropriate document reference for accessing or updating the HyDry device settings by evaluating the
 * presence of a clients administration reference, the ability to define own HyDry device settings, and an optional parameter indicating
 * a preference for using the caller's own reference.
 * 
 * If the clients administration reference is absent, access to define own HyDry device settings is granted, or the `ownRef`
 * parameter is true, the function defaults to using the specific client reference from `getClientRef()`. Otherwise, it
 * uses the clients administration reference. The returned reference points to the "hyDryDeviceSettings" document within a
 * "settings" collection, following the determined path in the database.
 * 
 * @param {boolean} [ownRef=false] - Optional. Indicates whether to prioritize the caller's own client reference over the general
 *                                   clients administration reference, defaulting to false.
 * @returns {FirebaseFirestore.DocumentReference} A Firestore document reference to the "hyDryDeviceSettings" document within
 *                                                the chosen path's "settings" collection.
 */
function getHyDryDeviceSettingsRef(ownRef?: boolean) {
  const clientsAdministrationRef = getClientsAdministrationRef();

  const documentRef =
    clientsAdministrationRef === "" ||
    defineOwnHyDryDeviceSettingsClientAccess() ||
    ownRef
      ? getClientRef()
      : clientsAdministrationRef;

  return db
    .doc(documentRef)
    .collection("settings")
    .doc("hyDryDeviceSettings");
}

/**
 * Establishes a real-time listener for updates to the HyDry device settings document within the database. Whenever
 * the document is updated, the specified callback function is invoked with the updated HyDry device settings.
 * 
 * This functionality is essential for applications that need to dynamically respond to changes in device settings,
 * ensuring that the latest configurations are always applied.
 *
 * @param {function(hyDryDeviceSettings: HyDryDeviceSettings): void} onNewHyDryDeviceSettings - Callback function to be executed
 *                                                                                              whenever the HyDry device settings
 *                                                                                              document is updated. The updated
 *                                                                                              settings are passed to this callback.
 * @returns {function} A function that, when called, unsubscribes from further updates to the HyDry device settings document,
 *                     allowing for cleanup and prevention of memory leaks when real-time updates are no longer necessary.
 */
export function observeHyDryDeviceSettings(
  onNewHyDryDeviceSettings: (hyDryDeviceSettings: HyDryDeviceSettings) => void
) {
  return getHyDryDeviceSettingsRef().onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const hyDryDeviceSettings = snapshot.data() as HyDryDeviceSettings;
      onNewHyDryDeviceSettings(hyDryDeviceSettings);
    }
  });
}

/**
 * Asynchronously updates the HyDry device settings in the database with the provided update data. The operation merges
 * the update object with the existing document, facilitating partial updates to the settings without overwriting other
 * fields that are not explicitly mentioned in the update.
 * 
 * This merge update strategy is particularly useful for making incremental changes to the device settings, preserving
 * existing configurations that are not being modified.
 *
 * @param {HyDryDeviceSettings} update - An object containing the updates for the HyDry device settings, specifying changes
 *                                       to be made. Fields not mentioned in the update object remain unaffected due to
 *                                       the merge strategy.
 * @returns {Promise<boolean>} A promise that resolves to true, indicating that the update operation has been successfully completed.
 */
export async function updateHyDryDeviceSettings(update: HyDryDeviceSettings) {
  // Filter out any properties that should not be updated directly
  if (update.areDefaultHyDryDeviceSettings !== undefined) {
    update.areDefaultHyDryDeviceSettings = undefined;
  }

  await getHyDryDeviceSettingsRef(true).set(filterOutUndefined(update), { merge: true });

  return true;
}

/**
 * Determines the minimum duration, in minutes, that a program chain should last, based on the current user settings.
 * This function seeks to establish the minimum acceptable duration for a program chain to ensure compliance with
 * user preferences or system-wide defaults. The preference is first extracted from the user's settings; if the user
 * has customized their settings and specified a minimum program chain duration, that value is used. If the user's
 * settings do not specify this duration, the function then falls back to a
 * predefined duration from local settings. In the absence of a specified minimum in the user settings, a default
 * value of 30 minutes is assumed.
 *
 * This approach accommodates reworked user settings, prioritizing them over local settings to reflect the most
 * current preferences or requirements. Local settings serve as a reliable fallback, ensuring backward compatibility
 * and a base level of functionality in scenarios where user settings are either default or unspecified.
 *
 * @returns {number} The minimum duration, in minutes, for a program chain. This value is directly influenced by
 *                   the user's current settings if available and explicitly defined, with local settings providing
 *                   a default in the absence of or as a fallback to user-specified preferences.
 */
export function getMinProgramChainDurationInMin() {
  const userSettings = UserSettingsModule.userSettings;

  const minProgramChainDurationFromLocalSettings =
    LocalSettingsModule.minProgramChainDurationInMin;
  const minProgramChainDurationFromUserSettings =
    userSettings.minProgramChainDurationInMin ?? 30;

  const minProgramChainDuration =
    userSettings && !userSettings.areDefaultUserSettings
      ? minProgramChainDurationFromUserSettings
      : minProgramChainDurationFromLocalSettings;

  return Math.max(0, minProgramChainDuration);
}

/**
 * Returns the minimum gap duration (in minutes) required to show a GENERAL entry.
 * Defaults to 5 minutes when not configured.
 */
export function getMinGeneralGapDurationInMin() {
  const userSettings = UserSettingsModule.userSettings;
  return Math.max(0, userSettings.minGeneralGapDurationInMin ?? 5);
}

/**
 * Retrieves the ComBox client configuration information based on the current device's HyDry settings.
 * For MINI devices, returns the MINI-specific config if available, falling back to the BOX config.
 * For BOX devices (default), returns the standard config.
 * If HyDry device settings are default or not found, falls back to legacy ComBox settings.
 *
 * @param isMini - Whether to return the MINI-specific client config. Defaults to false (BOX config).
 * @returns The client configuration information for the requested device type.
 */
export function getClientConfigInfo(isMini = false) {
  const hyDryDeviceSettings = HyDryDeviceSettingsModule.hyDryDeviceSettings;

  if (hyDryDeviceSettings && !hyDryDeviceSettings.areDefaultHyDryDeviceSettings) {
    if (isMini) {
      return hyDryDeviceSettings.miniClientConfigInfo
        ?? hyDryDeviceSettings.comBoxClientConfigInfo;
    }
    return hyDryDeviceSettings.comBoxClientConfigInfo;
  }

  return getComBoxClientConfigInfo();
}

/**
 * Whether the viewer's HyDry device settings hold a Mini client config of their own, rather than
 * the Box config `getClientConfigInfo(true)` falls back to (20 of 35 settings, 2026-10-30, were
 * saved before the Mini config existed).
 */
export function hasOwnMiniClientConfigInfo() {
  const hyDryDeviceSettings = HyDryDeviceSettingsModule.hyDryDeviceSettings;

  return !!(
    hyDryDeviceSettings &&
    !hyDryDeviceSettings.areDefaultHyDryDeviceSettings &&
    hyDryDeviceSettings.miniClientConfigInfo
  );
}

/**
 * Resolves the current `preferAbsHumidity` setting from CompanySettings,
 * falling back to ProjectManagerSettings if not defined.
 *
 * @returns Whether absolute humidity is preferred over mixing ratio. Defaults to `true`.
 */
function getPreferAbsHumidity(): boolean {
  const companySettings = CompanySettingsModule.companySettings;

  if (companySettings.preferAbsHumidity !== undefined) {
    return companySettings.preferAbsHumidity;
  }

  const projectManagerSettings =
    ProjectManagementSettingsModule.projectManagementSettings
      ?.projectManagerSettings;

  if (projectManagerSettings?.preferAbsHumidity !== undefined) {
    return projectManagerSettings.preferAbsHumidity;
  }

  return true;
}

/**
 * Reconciles the stored humidity unit with the current `preferAbsHumidity` setting.
 *
 * When a user toggles the humidity unit in IresDeviceDetail (e.g., to ABSOLUTE_HUMIDITY),
 * and then changes the preference in CompanySettings (e.g., to prefer MIXING_RATIO),
 * the stored unit would become stale. This function ensures the returned unit
 * always matches the current preference when a secondary (non-relative) unit is active.
 *
 * @param comBoxHumidityUnit The currently stored humidity unit.
 * @returns The humidity unit, corrected to match the current preferAbsHumidity setting if needed.
 */
function reconcileHumidityUnitWithPreference(
  comBoxHumidityUnit: ComBoxHumidityUnit
): ComBoxHumidityUnit {
  if (comBoxHumidityUnit === ComBoxHumidityUnit.RELATIVE_HUMIDITY) {
    return comBoxHumidityUnit;
  }

  const preferAbsHumidity = getPreferAbsHumidity();

  if (
    preferAbsHumidity &&
    comBoxHumidityUnit === ComBoxHumidityUnit.MIXING_RATIO
  ) {
    return ComBoxHumidityUnit.ABSOLUTE_HUMIDITY;
  }

  if (
    !preferAbsHumidity &&
    comBoxHumidityUnit === ComBoxHumidityUnit.ABSOLUTE_HUMIDITY
  ) {
    return ComBoxHumidityUnit.MIXING_RATIO;
  }

  return comBoxHumidityUnit;
}

/**
 * Retrieves the unit of humidity to be used in the ComBox, prioritizing the HyDry device settings over local settings.
 * This function selects the appropriate humidity unit for displaying or processing humidity data within the ComBox
 * interface. The returned value is one of the enum values from `ComBoxHumidityUnit`, which includes:
 * - `RELATIVE_HUMIDITY`: Indicates that humidity is expressed as a percentage of the maximum for the current temperature.
 * - `ABSOLUTE_HUMIDITY`: Represents the total water content in the air, regardless of temperature.
 * - `MIXING_RATIO`: Specifies humidity as the mass of water vapor in a unit mass of dry air.
 *
 * The selection logic first checks if the HyDry device settings specify a humidity unit. If so, the specified unit is used.
 * If the device settings do not specify a unit or are in their default state, the function resorts to the local settings.
 * Should the local settings also not specify a unit, `getDefaultHumidityUnit()` is called to provide a standard default
 * value, ensuring operational continuity.
 *
 * @returns {ComBoxHumidityUnit} The humidity unit for the ComBox, determined based on current configurations
 * from HyDry device settings, alternatively from local settings, or by a system-wide default. This ensures the
 * interface adapts to specific configurations, promoting accuracy and user preference in humidity data representation.
 */
export function getComBoxHumidityUnit() {
  const hyDryDeviceSettings = HyDryDeviceSettingsModule.hyDryDeviceSettings;

  const comBoxHumidityUnitFromLocalSettings =
    LocalSettingsModule.comBoxHumidityUnit;
  const comBoxHumidityUnitFromHyDryDeviceSettings =
    hyDryDeviceSettings.comBoxClientConfigInfo?.humidityUnit ??
    getDefaultHumidityUnit();

  const comBoxHumidityUnit =
    hyDryDeviceSettings && !hyDryDeviceSettings.areDefaultHyDryDeviceSettings
      ? comBoxHumidityUnitFromHyDryDeviceSettings
      : comBoxHumidityUnitFromLocalSettings;

  return reconcileHumidityUnitWithPreference(comBoxHumidityUnit);
}

/**
 * Retrieves the message settings for the HyDry system, prioritizing custom settings over default local settings.
 * This function determines which message settings should be used for the HyDry system operations within the interface.
 * The returned configuration object is selected based on the presence and customization status of the settings
 * from the HyDryMessagesSettingsModule and LocalSettingsModule. It includes:
 * - `areDefaultHyDryMessagesSettings`: A boolean indicating if the settings are the default ones provided by the system.
 * - Any other relevant configuration properties for the HyDry message settings.
 *
 * The selection logic first checks if the HyDry module's settings are customized (i.e., not default). If customized,
 * these settings are used. If the settings from the HyDry module are default or not specified, it falls back to the
 * local settings. This ensures the HyDry system uses the most relevant and customized settings available.
 *
 * @returns {HyDryMessagesSettings} The message settings for the HyDry system, determined based on the most relevant and up-to-date
 * configurations from either the HyDry messages settings module or local settings. This ensures operational
 * flexibility and customization based on user or system preferences.
 */
export function getHyDryMessagesSettings() {
  const hyDryMessagesSettingsFromLocalSettings =
    LocalSettingsModule.hyDryMessageSettings;
  const hyDryMessagesSettingsFromHyDryMessagesSettingsModule =
    HyDryMessagesSettingsModule.hyDryMessagesSettings;

  const hyDryMessagesSettings =
    hyDryMessagesSettingsFromHyDryMessagesSettingsModule &&
    !hyDryMessagesSettingsFromHyDryMessagesSettingsModule.areDefaultHyDryMessagesSettings
      ? hyDryMessagesSettingsFromHyDryMessagesSettingsModule
      : hyDryMessagesSettingsFromLocalSettings;

  return hyDryMessagesSettings;
}

/**
 * Sets the humidity unit to be used in the ComBox, updating either the HyDry device settings or the local settings
 * based on the current state of the HyDry device settings. This function takes a humidity unit as defined by the
 * `ComBoxHumidityUnit` enum, which includes options such as `RELATIVE_HUMIDITY`, `ABSOLUTE_HUMIDITY`, and
 * `MIXING_RATIO`, and applies it to the appropriate settings module.
 *
 * If the HyDry device settings are present, the specified humidity unit is saved to these device settings, allowing
 * for device-specific configuration. If the device settings are not available, the humidity unit is instead saved to
 * the local settings, serving as a general preference that applies when no specific device configuration is found.
 *
 * @param {ComBoxHumidityUnit} comBoxHumidityUnit - The humidity unit to be set for the ComBox. Must be one of the
 *                                                  predefined values in the `ComBoxHumidityUnit` enum.
 */
export function setComBoxHumidityUnit(comBoxHumidityUnit: ComBoxHumidityUnit) {
  const hyDryDeviceSettings = HyDryDeviceSettingsModule.hyDryDeviceSettings;

  if (
    hyDryDeviceSettings &&
    !hyDryDeviceSettings.areDefaultHyDryDeviceSettings
  ) {
    HyDryDeviceSettingsModule.setComBoxHumidityUnit(comBoxHumidityUnit);
  } else {
    LocalSettingsModule.setComBoxHumidityUnit(comBoxHumidityUnit);
  }
}

/**
 * Retrieves a Firestore document reference for project management settings, determining the appropriate reference based on
 * the context of client administration or specific client settings. This function evaluates several conditions
 * to decide whether to use a general client administration reference, a specific client reference, or fallback
 * to a default client reference. The decision is influenced by the presence or absence of a client administration
 * reference, the result of a function determining access to own project management settings, and an optional boolean parameter
 * that indicates a preference for using the caller's own reference.
 *
 * If the `clientsAdministrationRef` is empty, the `defineOwnProjectManagementSettingsClientAccess` function allows access,
 * or if the `ownRef` parameter is true, then the function uses the specific client reference obtained from
 * `getClientRef()`. Otherwise, it defaults to using the `clientsAdministrationRef`. The final document reference
 * points to a "projectManagementSettings" document within a "settings" collection of the chosen reference path in the database.
 *
 * @param {boolean} [ownRef=false] - Optional. Specifies whether to force the use of the caller's own client reference
 *                                   over the general client administration reference. Defaults to false, meaning
 *                                   the general administration reference is used unless other conditions apply.
 * @returns {FirebaseFirestore.DocumentReference} A Firestore document reference to the "projectManagementSettings" document
 *                                                within the determined reference path's "settings" collection.
 */
 function getProjectManagementSettingsRef(ownRef?: boolean) {
  const clientsAdministrationRef = getClientsAdministrationRef();

  const documentRef =
    clientsAdministrationRef === "" ||
    defineOwnProjectManagementSettingsClientAccess() ||
    ownRef
      ? getClientRef()
      : clientsAdministrationRef;

  return db
    .doc(documentRef)
    .collection("settings")
    .doc("projectManagementSettings");
}

/**
 * Sets up a real-time listener for changes to the project management settings document in the database. When the
 * project management settings document is updated, the provided callback function is invoked with the new settings data.
 *
 * @param {function(projectManagementSettings: ProjectManagementSettings): void} onNewProjectManagementSettings - A callback
 *                                                                                                                function that is called
 *                                                                                                                whenever the project management
 *                                                                                                                settings document is updated.
 *                                                                                                                The updated settings are passed
 *                                                                                                                to this callback as an argument.
 * @returns {function} A function that, when called, will unsubscribe from further document updates. This allows
 *                     for cleanup when the listener is no longer needed.
 */
 export function observeProjectManagementSettings(
  onNewProjectManagementSettings: (projectManagementSettings: ProjectManagementSettings) => void
) {
  return getProjectManagementSettingsRef().onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const projectManagementSettings = snapshot.data() as ProjectManagementSettings;
      onNewProjectManagementSettings(projectManagementSettings);
    }
  });
}

/**
 * Reads the project management settings once into `ProjectManagementSettingsModule`, unless they are
 * there already.
 *
 * For the route guard, which decides whether "Neues Projekt" may open from them: the observer above
 * is started by `Scaffold`, which cannot mount before the guard has resolved, so on a hard page load
 * the module still holds the defaults. Same reasoning as `ensureRoleSettingsLoaded`.
 *
 * A failed read leaves the module unloaded, so the next navigation tries again.
 */
export async function ensureProjectManagementSettingsLoaded(): Promise<void> {
  if (ProjectManagementSettingsModule.hasLoaded) {
    return;
  }

  try {
    const snapshot = await getProjectManagementSettingsRef().get();

    if (snapshot.exists) {
      ProjectManagementSettingsModule.setProjectManagementSettings(
        snapshot.data() as ProjectManagementSettings
      );
    } else {
      ProjectManagementSettingsModule.markLoaded();
    }
  } catch {
    // Leave the defaults; the guard's check stays fail-closed for creating projects.
  }
}

/**
 * Asynchronously updates the project management settings in the database with the provided update data. This function performs
 * a merge update, combining the existing document with the provided update object. It facilitates partial updates, ensuring
 * that only the specified fields in the update object are modified without altering other existing fields.
 *
 * @param {ProjectManagementSettings} update - An object containing the new values for the project management settings. The update object
 *                                             may include partial fields from the ProjectManagementSettings. Fields not specified in this
 *                                             update will remain unchanged due to the merge operation. Any undefined values within the
 *                                             'update' object are intentionally filtered out before the database update to avoid overwriting
 *                                             existing values with undefined.
 * @returns {Promise<boolean>} A promise that resolves to true if the update operation is completed successfully. This indicates
 *                             that the merge update has been applied to the database.
 */
 export async function updateProjectManagementSettings(update: ProjectManagementSettings) {
  if (update.areDefaultProjectManagementSettings !== undefined) {
    update.areDefaultProjectManagementSettings = undefined;
  }

  // Projektorganisation is set in the IRES Admin Client only (2026-10-29): never written from here.
  await getProjectManagementSettingsRef(true).set(
    filterOutUndefined(withoutProjectOrganisation(update)),
    { merge: true }
  );

  return true;
}

/**
 * Retrieves a Firestore document reference for all devices settings, determining the appropriate reference based on
 * the context of client administration or specific client settings. This function evaluates several conditions
 * to decide whether to use a general client administration reference, a specific client reference, or fallback
 * to a default client reference. The decision is influenced by the presence or absence of a client administration
 * reference, the result of a function determining access to own device settings, and an optional boolean parameter
 * that indicates a preference for using the caller's own reference.
 *
 * If the `clientsAdministrationRef` is empty, the `defineOwnAllDevicesSettingsClientAccess` function allows access,
 * or if the `ownRef` parameter is true, then the function uses the specific client reference obtained from
 * `getClientRef()`. Otherwise, it defaults to using the `clientsAdministrationRef`. The final document reference
 * points to a "allDevicesSettings" document within a "settings" collection of the chosen reference path in the database.
 *
 * @param {boolean} [ownRef=false] - Optional. Specifies whether to force the use of the caller's own client reference
 *                                   over the general client administration reference. Defaults to false, meaning
 *                                   the general administration reference is used unless other conditions apply.
 * @returns {FirebaseFirestore.DocumentReference} A Firestore document reference to the "allDevicesSettings" document
 *                                                within the determined reference path's "settings" collection.
 */
 function getAllDevicesSettingsRef(ownRef?: boolean) {
  const clientsAdministrationRef = getClientsAdministrationRef();

  const documentRef =
    clientsAdministrationRef === "" ||
    defineOwnAllDevicesSettingsClientAccess() ||
    ownRef
      ? getClientRef()
      : clientsAdministrationRef;

  return db
    .doc(documentRef)
    .collection("settings")
    .doc("allDevicesSettings");
}

/**
 * Sets up a real-time listener for changes to the all devices settings document in the database. When the
 * all devices settings document is updated, the provided callback function is invoked with the new settings data.
 *
 * @param {function(allDevicesSettings: AllDevicesSettings): void} onNewAllDevicesSettings - A callback
 *                                                                                          function that is called
 *                                                                                          whenever the all devices
 *                                                                                          settings document is updated.
 *                                                                                          The updated settings are passed
 *                                                                                          to this callback as an argument.
 * @returns {function} A function that, when called, will unsubscribe from further document updates. This allows
 *                     for cleanup when the listener is no longer needed.
 */
export function observeAllDevicesSettings(
  onNewAllDevicesSettings: (allDevicesSettings: AllDevicesSettings) => void
) {
  return getAllDevicesSettingsRef().onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const data = snapshot.data();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { projectDevicesSettings, importConfiguration } = data as any;

      // Map specific keys for importConfiguration if they exist
      const processedImportConfiguration = {
        ...importConfiguration,
        defaultImportConfiguration: importConfiguration?.isDefaultImportConfiguration ?? importConfiguration?.defaultImportConfiguration,
        newCategoryAllowed: importConfiguration?.isNewCategoryAllowed ?? importConfiguration?.newCategoryAllowed,
        newTemplateAllowed: importConfiguration?.isNewTemplateAllowed ?? importConfiguration?.newTemplateAllowed,
        updateAllowed: importConfiguration?.isUpdateAllowed ?? importConfiguration?.updateAllowed,
      };

      // Pass the processed data to the callback
      onNewAllDevicesSettings({
        projectDevicesSettings,
        importConfiguration: processedImportConfiguration,
      } as AllDevicesSettings);
    }
  });
}

/**
 * Retrieves a reference to the Firestore document containing device categories and templates.
 * This can either be a reference to the client's specific document or to a shared administration document,
 * depending on the parameters and the environment setup.
 *
 * @param {boolean} [ownRef=false] - Optional flag to determine if the reference should be to the client's own document.
 * @returns {DocumentReference} Firestore document reference to either the client-specific or shared device categories and templates.
 */
function getDeviceCategoriesAndTemplatesRef(ownRef?: boolean) {
  const clientsAdministrationRef = getClientsAdministrationRef();
  const isClientRef =
    ownRef ||
    clientsAdministrationRef === "" ||
    defineCategoriesAndTemplatesClientAccess();

  const documentRef = isClientRef ? getClientRef() : clientsAdministrationRef;

  return db
    .doc(documentRef)
    .collection(isClientRef ? "deviceSettings" : "settings")
    .doc(isClientRef ? getClientId() : "deviceCategoriesAndTemplates");
}

/**
 * Sets up a real-time observer for changes to local device categories and templates in Firestore.
 * This function listens for updates on the client's device settings and notifies via a callback when updates occur.
 *
 * @param {Function} onNewLocalDeviceCategoriesAndTemplates - Callback to handle the data received from the snapshot.
 * @returns {Function} Returns the unsubscribe function from Firestore's onSnapshot method, which can be used to stop listening for changes.
 */
export function observeLocalDeviceCategoriesAndTemplates(
  onNewLocalDeviceCategoriesAndTemplates: (deviceCategoriesAndTemplates: DeviceCategoriesAndTemplates) => void
) {
  return getDeviceCategoriesAndTemplatesRef(true).onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const deviceCategoriesAndTemplates = snapshot.data() as DeviceCategoriesAndTemplates;
      onNewLocalDeviceCategoriesAndTemplates(deviceCategoriesAndTemplates);
    }
  });
}

/**
 * Sets up a real-time observer for changes to shared device categories and templates in Firestore.
 * This function listens for updates on shared device settings across clients and notifies via a callback when updates occur.
 *
 * @param {Function} onNewSharedDeviceCategoriesAndTemplates - Callback to handle the data received from the snapshot.
 * @returns {Function} Returns the unsubscribe function from Firestore's onSnapshot method, which can be used to stop listening for changes.
 */
export function observeSharedDeviceCategoriesAndTemplates(
  onNewSharedDeviceCategoriesAndTemplates: (deviceCategoriesAndTemplates: DeviceCategoriesAndTemplates) => void
) {
  return getDeviceCategoriesAndTemplatesRef().onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const deviceCategoriesAndTemplates = snapshot.data() as DeviceCategoriesAndTemplates;
      onNewSharedDeviceCategoriesAndTemplates(deviceCategoriesAndTemplates);
    }
  });
}

/**
 * A one-shot read of a client's own device categories, by client id, for a caller that needs the
 * category names of a client other than the signed-in one (a group master naming its members'
 * category mix). Reads the client's own `deviceSettings/{clientId}` document, the same document the
 * local-settings observer reads for the signed-in client, so it returns the categories a client that
 * defines its own would see. A client that inherits the group's shared categories keeps no own
 * document (or an unused one), and its ids are named by the shared / default set instead; because a
 * member's own-document ids and the shared document's ids are independent id spaces, reading an own
 * document that the member does not actually use yields no name the member's breakdown ever asks for.
 *
 * Returns `[]` when the client has no own document, so a caller can merge the result unconditionally.
 *
 * @param {string} clientId - the client whose own device categories to read.
 * @returns {Promise<DeviceCategoryTemplate[]>} the stored categories, or `[]` where there are none.
 */
export async function getDeviceCategoriesOfClient(clientId: string): Promise<DeviceCategoryTemplate[]> {
  const snapshot = await db
    .doc(getClientRefById(clientId))
    .collection("deviceSettings")
    .doc(clientId)
    .get();

  if (!snapshot.exists) {
    return [];
  }

  const deviceCategoriesAndTemplates = snapshot.data() as DeviceCategoriesAndTemplates;
  const deviceSettingsJson = JSON.parse(deviceCategoriesAndTemplates.deviceSettingsJson) as DeviceSettingsJson;
  return deviceSettingsJson.deviceCategories ?? [];
}

/**
 * Asynchronously updates the settings for all devices in the database with the provided update data. This function performs
 * a merge update, combining the existing document with the provided update object. It facilitates partial updates, ensuring
 * that only the specified fields in the update object are modified without altering other existing fields.
 *
 * @param {AllDevicesSettings} update - An object containing the new values for the devices settings. The update object
 *                                      may include partial fields from the AllDevicesSettings. Fields not specified in this
 *                                      update will remain unchanged due to the merge operation. Any undefined values within the
 *                                      'update' object are intentionally filtered out before the database update to avoid overwriting
 *                                      existing values with undefined.
 * @returns {Promise<boolean>} A promise that resolves to true if the update operation is completed successfully. This indicates
 *                             that the merge update has been applied to the database.
 */
 export async function updateAllDevicesSettings(update: AllDevicesSettings) {
  // Filter out any properties that should not be updated directly
  if (update.areDefaultAllDevicesSettings !== undefined) {
    update.areDefaultAllDevicesSettings = undefined;
  }

  await getAllDevicesSettingsRef(true).set(
    filterOutUndefined(update),
    { merge: true }
  );

  return true;
}